import { ApolloClient, ApolloLink, HttpLink } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import * as Sentry from '@sentry/react';

import { QP_QUERY_URI } from '@constants/config';
import { APPOINTMENT_TOKEN } from '@constants/storageKeys';
import { traceContextManager } from '@services/otel/TraceContextManager';

import { sseLink } from './SSELink';
import { TraceLink } from './TraceLink';
import { cacheConfig } from './cacheConfig';

const getAppointmentToken = () => localStorage.getItem(APPOINTMENT_TOKEN);

// Fetch policies for background data loading
const defaultOptions: ApolloClient.DefaultOptions = {
  watchQuery: {
    fetchPolicy: 'cache-and-network', // For useQuery: return cache immediately, fetch in background
  },
  query: {
    fetchPolicy: 'cache-first', // For one-time queries: use cache if available
  },
};

const errorLink = new ErrorLink(({ error, operation }) => {
  const traceparent = operation.getContext().headers?.traceparent;
  const operationName = operation.operationName;

  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, locations, path }) => {
      const err = new Error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );
      Sentry.captureException(err, { tags: { traceparent, operationName } });
    });
  } else {
    const err = new Error(
      `[Network error]: ${error}, Operation Name: ${operationName}`
    );
    Sentry.captureException(err, { tags: { traceparent, operationName } });
  }
});

const authLink = (getToken: () => Promise<Nullable<string>>) =>
  new SetContextLink(async ({ headers }) => {
    const token = await getToken();
    const appointmentToken = getAppointmentToken();
    // We strip auth header coming from the browser context, because it carry BasicAuth
    // This may interfere with our Bearer token
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { authorization, ...restHeaders } = headers ?? {};

    return {
      headers: {
        ...restHeaders,
        ...(appointmentToken
          ? { 'x-appointment-token': appointmentToken }
          : {}),
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

// If we are in development mode and the use basket api proxy flag is set, use the proxy basket API URI.
// This makes it possible to progress the in-life journey with a valid user session.
const BASKET_API_URI =
  import.meta.env.DEV && import.meta.env.VITE_USE_BASKET_API_PROXY === 'true'
    ? '/basket/api/query'
    : QP_QUERY_URI;

const splitLink = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  sseLink,
  new HttpLink({ uri: BASKET_API_URI })
);

export const createClient = (getToken: () => Promise<Nullable<string>>) => {
  const traceLink = new TraceLink(() =>
    traceContextManager.getCurrentTraceparent()
  );

  return new ApolloClient({
    cache: cacheConfig,
    defaultOptions,
    link: ApolloLink.from([
      traceLink,
      errorLink,
      authLink(getToken),
      splitLink,
    ]),
  });
};
